// Staff card: invite a candidate to record a 60-second self-introduction, play it, delete it.
// Track A / A7, 6 Sep 2026. Embed on CandidateDetailPage:
//   import VideoIntroCard from '../components/candidates/VideoIntroCard';
//   <VideoIntroCard candidateId={candidate.id} requirementId={selectedRequirementId} />
// Backend: routes/video_intro.py (/api/video-intro/*). The stream route needs the staff Bearer token,
// so the video is fetched as a blob through the axios instance and played from an object URL.
// A7b (6 Sep 2026): "Send invite" / "Remind" open SendInviteDialog, which sends through
// routes/assessment_invites.py. "Copy link" still works exactly as before.
import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Bell, Copy, Loader2, Mail, Play, RefreshCw, Send, ShieldCheck, Trash2, Video, XCircle } from 'lucide-react';
import SendInviteDialog, { InviteHistory } from './SendInviteDialog';

const STATUS_VARIANT = { invited: 'secondary', uploaded: 'default', expired: 'destructive', cancelled: 'destructive' };

function fmt(d) { return d ? new Date(d).toLocaleString() : '-'; }
function mb(n) { return n ? `${(n / (1024 * 1024)).toFixed(1)} MB` : '-'; }

export default function VideoIntroCard({ candidateId, requirementId }) {
  const [enabled, setEnabled] = useState(null);
  const [invites, setInvites] = useState([]);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);      // { id, url }
  const [loadingId, setLoadingId] = useState(null);
  const [sendFor, setSendFor] = useState(null);    // { recordId, purpose }
  const [sentTick, setSentTick] = useState(0);

  const load = useCallback(async () => {
    if (!candidateId) return;
    try {
      const s = await api.get('/video-intro/settings');
      setEnabled(!!s.data.enabled);
      if (!s.data.enabled) return;
      const [i, v] = await Promise.all([
        api.get('/video-intro/invites', { params: { candidate_id: candidateId } }),
        api.get('/video-intro/videos', { params: { candidate_id: candidateId } }),
      ]);
      setInvites(i.data.invites || []);
      setVideos(v.data.videos || []);
    } catch (e) {
      if (e?.response?.status === 503) setEnabled(false);
      else toast.error(e?.response?.data?.detail || 'Could not load video introductions');
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (playing?.url) URL.revokeObjectURL(playing.url); }, [playing]);

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.post('/video-intro/invites', { candidate_id: candidateId, requirement_id: requirementId || null });
      toast.success(r.data.reused ? 'An active invite already exists - reusing it' : 'Recording link created (valid 7 days)');
      if (r.data.record_url && navigator.clipboard) { try { await navigator.clipboard.writeText(r.data.record_url); toast.info('Link copied to clipboard'); } catch { /* noop */ } }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create invite'); } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this recording link?')) return;
    try { await api.post(`/video-intro/invites/${id}/cancel`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
  };

  const play = async (v) => {
    if (playing?.id === v.id) { setPlaying(null); return; }
    setLoadingId(v.id);
    try {
      const r = await api.get(`/video-intro/videos/${v.id}/stream`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      setPlaying({ id: v.id, url, type: v.content_type });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load the video'); } finally { setLoadingId(null); }
  };

  const remove = async (v) => {
    const reason = window.prompt('Delete this video permanently? Optional reason:', 'candidate request');
    if (reason === null) return;
    try {
      await api.delete(`/video-intro/videos/${v.id}`, { data: { reason } });
      if (playing?.id === v.id) setPlaying(null);
      toast.success('Video deleted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch { window.prompt('Copy this link', url); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Video className="h-4 w-4" />Video introduction</CardTitle>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
          {enabled && <Button size="sm" onClick={invite} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />Invite</>}</Button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {enabled === false && <p className="text-muted-foreground">Video introductions are switched off (Settings: video_intro_enabled).</p>}
        {enabled && (
          <>
            {videos.length === 0 && invites.length === 0 && (
              <p className="text-muted-foreground">No video yet. Invite creates a 7-day link; send it from here by email or WhatsApp, or copy it and send it yourself.</p>
            )}
            {videos.map((v) => (
              <div key={v.id} className="border rounded-md p-2 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>video</Badge>
                  <span>{v.duration_seconds ? `${Math.round(v.duration_seconds)}s` : '-'} - {mb(v.size_bytes)} - {v.content_type}</span>
                  <span className="text-xs text-muted-foreground ml-auto">uploaded {fmt(v.uploaded_at)}</span>
                  <Button variant="outline" size="sm" onClick={() => play(v)} disabled={loadingId === v.id}>
                    {loadingId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1" />{playing?.id === v.id ? 'Hide' : 'Play'}</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(v)} title="Delete permanently"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> consent {v.consent_text_version} at {fmt(v.consented_at)}{v.consent_ip ? ` from ${v.consent_ip}` : ''}
                </div>
                {playing?.id === v.id && <video src={playing.url} controls playsInline className="w-full max-w-lg aspect-video rounded-md bg-black" />}
              </div>
            ))}
            {invites.filter((i) => i.status !== 'uploaded').map((i) => (
              <div key={i.id} className="border rounded-md p-2 flex flex-wrap items-center gap-2">
                <Badge variant={STATUS_VARIANT[i.status] || 'secondary'}>{i.status}</Badge>
                <span className="text-xs text-muted-foreground">created {fmt(i.created_at)} - expires {fmt(i.expires_at)}</span>
                {i.status === 'invited' && (
                  <>
                    <Button variant="outline" size="sm" className="ml-auto" onClick={() => setSendFor({ recordId: i.id, purpose: 'invite' })} title="Send this link to the candidate">
                      <Mail className="h-4 w-4 mr-1" />Send invite
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSendFor({ recordId: i.id, purpose: 'reminder' })} title="One polite reminder (blocked inside 24 hours)">
                      <Bell className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {i.record_url && <Button variant="ghost" size="sm" onClick={() => copy(i.record_url)} title="Copy link"><Copy className="h-4 w-4" /></Button>}
                {i.status === 'invited' && <Button variant="ghost" size="sm" onClick={() => cancel(i.id)} title="Cancel link"><XCircle className="h-4 w-4" /></Button>}
              </div>
            ))}
            <InviteHistory candidateId={candidateId} kind="video" refreshKey={sentTick} />
          </>
        )}
      </CardContent>
      <SendInviteDialog
        open={!!sendFor} onClose={() => setSendFor(null)}
        candidateId={candidateId} kind="video"
        recordId={sendFor?.recordId} purpose={sendFor?.purpose || 'invite'}
        onSent={() => { setSentTick((n) => n + 1); load(); }}
      />
    </Card>
  );
}
